import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import classNames from 'classnames';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ActivityObjectType,
  EntryType,
  Nutrition,
  NutritionConstraints,
  ProgrammePlan,
} from '../../API';
import { deduplicate, flattenObjects } from '../../services/arrays';
import {
  sumNutrition,
  targetDayNutritionFromMetricConstraints,
  macroRatioFor,
} from '../../services/nutrition';
import {
  PROGRAMMES_DEFAULT_TARGET_CALORIES,
  planEntries,
  planEntryByEntryID,
  programmeEntryByEntryID,
  programmePlanDayIndex,
  programmePlanObjectIDsOfType,
  programmePlanWithEntryAdded,
  programmeWithPlanUpdated,
} from '../../services/programmes';
import Board from '../common/board';
import { useIsMobile } from '../common/layout_hooks';
import GRCRecipeDetailContainer from '../meal/grc_recipe_detail_container';
import MealDetailContainer from '../meal/meal_detail_container';
import NoteDetailContainer from '../planner/note_detail_container';
import AddProgrammePlanCardLink from './add_programme_plan_card_link';
import ContentEntryDetailContainer from './content_entry_detail_container';
import ProgrammeEntryCard from './programme_entry_card';
import ProgrammePlanDayHeader from './programme_plan_day_header';
import usePrevious from '../../services/use_previous';
import { scrollNewObjectCardIntoView } from '../../services/smorg_board';
import {
  useDeleteNoteMutation,
  useGetContentEntriesByParentQuery,
  useGetGRCRecipesByIDsQuery,
  useGetDistilledMealsByParentsQuery,
  useGetNotesByParentQuery,
  useGetProgrammesQuery,
  useGetSharedContentEntriesByParentsQuery,
  useGetSpaceQuery,
  useCreateContentEntryMutation,
  useUpdateProgrammeMutation,
  useDeleteContentEntryMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import {
  useGetOrCreateSpaceContentBoardsQuery,
  useGetUserProfileQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import { healthProGroupForSpaceID } from '../../services/users';
import { useTracker } from '../../operations/tracking_operations';
import ResourceLoadingModal from '../common/resource_loading_modal';
import { userFriendlyNameForActivitySignalSelector } from '../../features/user_id/user_id_slice';
import { reportAccountActivitySignalOperation } from '../../operations/user_profile_operations';

interface ProgrammeBoardProps {
  programmeId: string;
  programmePlan: ProgrammePlan;
  nutritionConstraints: NutritionConstraints | null | undefined;
  showNutritionToUsers: boolean | null | undefined;
}

const components = {
  Card: ProgrammeEntryCard,
  LaneHeader: ProgrammePlanDayHeader,
  AddCardLink: AddProgrammePlanCardLink,
};

const labels: Record<string, unknown> = {
  'Add another lane': '+ Add another menu',
  'Delete lane': 'Delete menu',
  'Lane actions': 'Menu actions',
  button: {
    'Add lane': 'Add menu',
    'Add card': 'Add meal',
    Cancel: 'Cancel',
  },
  placeholder: {
    title: 'title',
    description: 'description',
    label: 'label',
  },
};

const customTranslation = (key: string) => labels[key];

const boardCss = { textAlign: 'left' };
const mobileHeightRule = { height: 'calc(var(--app-height) - 119px)' };
const desktopHeightRule = { height: 'calc(var(--app-height) - 100px)' };
const mobileBoardCss = { ...boardCss, ...mobileHeightRule };
const desktopBoardCss = { ...boardCss, ...desktopHeightRule };

const ProgrammePlanBoard = ({
  programmeId,
  programmePlan,
  nutritionConstraints,
  showNutritionToUsers,
}: ProgrammeBoardProps) => {
  const hasNutritionHeader = showNutritionToUsers;

  const tracker = useTracker();

  const { data: userProfile, isLoading: isUserProfileLoading } =
    useGetUserProfileQuery();

  const { data: space, isLoading: isSpaceLoading } = useGetSpaceQuery();

  const currentHealthProGroup = space && healthProGroupForSpaceID(space.id);

  const [createContentEntry] = useCreateContentEntryMutation();

  const [updateProgramme] = useUpdateProgrammeMutation();

  const { data: ownedProgrammes, isLoading: isOwnedProgrammesLoading } =
    useGetProgrammesQuery(undefined);

  const programme = (ownedProgrammes || []).find((p) => p.id === programmeId);

  const {
    data: distilledMealsByParents,
    isLoading: isDistilledMealsByParentsLoading,
  } = useGetDistilledMealsByParentsQuery(programme?.recipesBoardIDs || [], {
    skip: (programme?.recipesBoardIDs || []).length === 0,
  });

  const distilledMeals = flattenObjects(
    Object.values(distilledMealsByParents || {}),
  );

  const { data: notes, isLoading: notesLoading } = useGetNotesByParentQuery(
    programme?.id || '',
    {
      skip: !programme?.id,
    },
  );

  const { data: contentEntries, isLoading: contentEntriesLoading } =
    useGetContentEntriesByParentQuery(programme?.id || '', {
      skip: !programme?.id,
    });

  const { data: sharedContentBoards, isLoading: sharedContentBoardsLoading } =
    useGetOrCreateSpaceContentBoardsQuery();

  const sharedContentBoardIDs = useMemo(
    () => (sharedContentBoards || []).map((board) => board.id),
    [sharedContentBoards],
  );

  const {
    data: sharedContentEntriesByParents,
    isLoading: isSharedContentEntriesByParentsLoading,
  } = useGetSharedContentEntriesByParentsQuery(sharedContentBoardIDs, {
    skip: sharedContentBoardIDs.length === 0,
  });

  const [deleteNote] = useDeleteNoteMutation();

  const [deleteContentEntry] = useDeleteContentEntryMutation();

  const sharedContentEntries = flattenObjects(
    Object.values(sharedContentEntriesByParents || {}),
  );

  const allEntryIDs = programmePlan
    ? (planEntries(programmePlan) || []).map((e) => e.id)
    : [];
  const memoizedEntryIDs = useMemo(
    () => allEntryIDs,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(allEntryIDs)],
  );
  const previousEntryIDs = usePrevious(memoizedEntryIDs);

  // console.log({ memoizedEntryIDs, previousEntryIDs });

  useEffect(() => {
    scrollNewObjectCardIntoView(memoizedEntryIDs, previousEntryIDs);
  }, [memoizedEntryIDs, previousEntryIDs]);

  const navigate = useNavigate();

  const { entryId } = useParams();

  const detailVisibleFor = useMemo(() => {
    if (!entryId || !programmePlan.days) {
      return null;
    }
    const entry = planEntryByEntryID(programmePlan, entryId);
    if (!entry) {
      return null;
    }
    return {
      entryID: entryId,
      parentID: programmeId,
      entryType: entry.programmeEntryType,
      objectID: entry.objectID,
    };
  }, [entryId, programmeId, programmePlan]);

  const preferredMetrics = deduplicate(
    (nutritionConstraints?.nutritionMetricConstraints || []).map(
      (c) => c.nutritionMetric,
    ),
  ).slice(0, 3);

  if (!preferredMetrics.includes('calories')) {
    preferredMetrics.unshift('calories');
  }

  const preferredMetricsWithRules = preferredMetrics.map((metricName) => ({
    metricName,
    rules: [] /* (nutritionConstraints?.nutritionMetricConstraints || [])
      .filter((c) => c.nutritionMetric === metricName)
      .map((c) => ({
        operator: c.operator,
        value: c.value,
        units: c.units,
      })) */,
  }));

  const grcRecipeIDs = programmePlanObjectIDsOfType(
    programmePlan,
    EntryType.GRC_RECIPE,
  );

  const { data: grcRecipes, isLoading: isGrcRecipesLoading } =
    useGetGRCRecipesByIDsQuery(grcRecipeIDs || [], {
      skip: !grcRecipeIDs || grcRecipeIDs.length === 0,
    });

  const reactTrelloData = {
    lanes: programmePlan.days.map((day) => {
      const targetNutrition = targetDayNutritionFromMetricConstraints(
        nutritionConstraints?.nutritionMetricConstraints || [],
        preferredMetrics,
        PROGRAMMES_DEFAULT_TARGET_CALORIES,
      );

      const cards = day.entries
        .map((entry) => {
          if (entry.programmeEntryType === EntryType.MEAL) {
            const distilledMeal = (distilledMeals || {})[entry.objectID];
            if (!distilledMeal) {
              // Meal not yet loaded
              return null;
            }
            return {
              id: entry.id,
              title: distilledMeal.recipes[0].title,
              description: distilledMeal.recipes[0].shortDescription || '',
              style: {
                _programmeEntryType: entry.programmeEntryType,
                _objectID: entry.objectID,
                _programmeID: programmeId,
                _preferredMetricsWithRules: preferredMetricsWithRules,
                _targetNutrition: targetNutrition,
                _targetCalories: PROGRAMMES_DEFAULT_TARGET_CALORIES,
              },
            };
          }

          if (entry.programmeEntryType === EntryType.GRC_RECIPE) {
            const grcRecipe = (grcRecipes || {})[entry.objectID];
            if (!grcRecipe) {
              // GRC recipe not yet loaded
              return null;
            }
            return {
              id: entry.id,
              title: grcRecipe.recipe.title,
              description: grcRecipe.recipe.shortDescription || '',
              style: {
                _programmeEntryType: entry.programmeEntryType,
                _objectID: entry.objectID,
                _programmeID: programmeId,
                _preferredMetricsWithRules: preferredMetricsWithRules,
                _targetNutrition: targetNutrition,
                _targetCalories: PROGRAMMES_DEFAULT_TARGET_CALORIES,
              },
            };
          }

          if (entry.programmeEntryType === EntryType.NOTE) {
            const note = (notes || {})[entry.objectID];
            if (!note) {
              // Note not yet loaded
              return null;
            }
            return {
              id: entry.id,
              title: note.title,
              description: note.description || '',
              style: {
                _programmeEntryType: entry.programmeEntryType,
                _objectID: entry.objectID,
                _programmeID: programmeId,
              },
            };
          }

          if (entry.programmeEntryType === EntryType.CONTENT_ENTRY) {
            const contentEntry = (contentEntries || {})[entry.objectID];
            if (!contentEntry) {
              // Content entry not yet loaded
              return null;
            }
            return {
              id: entry.id,
              title: contentEntry.title,
              description: contentEntry.body || '',
              style: {
                _programmeEntryType: entry.programmeEntryType,
                _objectID: entry.objectID,
                _programmeID: programmeId,
              },
            };
          }

          if (
            entry.programmeEntryType ===
            EntryType.SHARED_CONTENT_ENTRY_REFERENCE
          ) {
            const sharedContentEntry = sharedContentEntries[entry.objectID];
            if (!sharedContentEntry) {
              // Shared content entry not yet loaded
              return null;
            }
            return {
              id: entry.id,
              title: sharedContentEntry.title,
              description: sharedContentEntry.body || '',
              style: {
                _programmeEntryType: entry.programmeEntryType,
                _objectID: entry.objectID,
                _programmeID: programmeId,
              },
            };
          }

          return null;
        })
        .filter((cardData) => !!cardData);

      const titleStyle: Record<string, any> = { _isPreview: true };

      const allNutritionEntries = day.entries.map((entry) => {
        if (entry.programmeEntryType === EntryType.MEAL) {
          const meal = (distilledMeals || {})[entry.objectID];
          return meal?.derivedNutrition?.totalNutritionPerServing as Nutrition;
        }

        if (entry.programmeEntryType === EntryType.GRC_RECIPE) {
          // const grcRecipe = (grcRecipes || {})[entry.objectID];
          // TODO - what does this object look like?
        }
        return {};
      });

      const actualTotalNutrition = allNutritionEntries.reduce(
        (acc, nutrition) => sumNutrition(acc, nutrition || {}) as Nutrition,
        {},
      );

      const actualMacroRatio = actualTotalNutrition
        ? macroRatioFor(actualTotalNutrition)
        : {};

      if (hasNutritionHeader) {
        titleStyle._nutritionHeader = {
          targetNutrition,
          actualTotalNutrition,
          preferredMetricsWithRules,
          targetCalories: PROGRAMMES_DEFAULT_TARGET_CALORIES,
          actualMacroRatio,
          showNutritionToUsers,
        };
      }

      return {
        id: day.id,
        title: day.title,
        titleStyle,
        cards,
      };
    }),
  };

  // console.log(reactTrelloData);

  // const isBlank = programmePlan.days.every((day) => day.entries.length === 0);

  const onCardClick = useCallback(
    (cardId: string, metadata: object, laneId: string) => {
      console.log({ cardId, metadata, laneId });
      navigate(
        `/programmes/${programmeId}/plans/${programmePlan.id}/entry/${cardId}`,
      );
    },
    [navigate, programmeId, programmePlan.id],
  );

  const friendlyUsername = useSelector(
    userFriendlyNameForActivitySignalSelector,
  );

  const afterCardDrag = useCallback(
    async (
      cardId: string,
      sourceLaneId: string,
      targetLaneId: string,
      position: number,
      /* cardDetails: object, */
    ) => {
      if (!programme) {
        return false;
      }
      const sourceDay = programmePlan.days.find((d) => d.id === sourceLaneId);
      if (!sourceDay) {
        return false;
      }
      const entry = sourceDay.entries.find((e) => e.id === cardId);
      if (!entry) {
        return false;
      }
      const planDays = programmePlan.days.map((day) => {
        let updatedDay = day;
        if (day.id === sourceLaneId) {
          updatedDay = {
            ...updatedDay,
            entries: day.entries.filter((e) => e.id !== cardId),
          };
        }
        if (day.id === targetLaneId) {
          const updatedEntries = [...updatedDay.entries];
          updatedEntries.splice(position, 0, entry);
          updatedDay = {
            ...updatedDay,
            entries: updatedEntries,
          };
        }
        return updatedDay;
      });
      const updatedProgramme = programmeWithPlanUpdated(programme, {
        ...programmePlan,
        days: planDays,
      });
      await updateProgramme(updatedProgramme);
      reportAccountActivitySignalOperation({
        spaceID: space?.id,
        friendlyUsername,
        lastActivityObjectType: ActivityObjectType.PROGRAMMES,
        lastActivityObjectID: programme.id,
      });
      // Cancel the drop, because the board will re-render with the new structure.
      return false;
    },
    [friendlyUsername, programme, programmePlan, space?.id, updateProgramme],
  );

  const afterCardAdd = useCallback(
    async (reactTrelloCard: Record<string, any>, laneId: string) => {
      console.log({ reactTrelloCard });

      if (!programme) {
        return;
      }

      const dayIndex = programmePlanDayIndex(laneId, programmePlan.days);
      if (dayIndex === null) {
        return;
      }

      const newContentEntry = reactTrelloCard.style._contentEntry;
      if (newContentEntry) {
        try {
          const input = { ...newContentEntry };
          input.parentID = programmeId;
          input.groups = currentHealthProGroup ? [currentHealthProGroup] : null;
          const { data: contentEntry } = await createContentEntry(input);
          if (!contentEntry) {
            return;
          }
          const entryID = uuidv4();
          const updatedProgramme = programmeWithPlanUpdated(
            programme,
            programmePlanWithEntryAdded(
              programmePlan,
              [laneId],
              entryID,
              EntryType.CONTENT_ENTRY,
              contentEntry.id,
            ),
          );
          tracker(
            userProfile,
            [
              {
                name: 'Add new content entry',
                args: {
                  title: newContentEntry.title,
                  section: 'programmes',
                },
              },
            ],
            ['numContentEntriesAdded'],
          );
          await updateProgramme(updatedProgramme);
          navigate(
            `/programmes/${programmeId}/plans/${programmePlan.id}/entry/${entryID}`,
          );
        } finally {
          reportAccountActivitySignalOperation({
            spaceID: space?.id,
            friendlyUsername,
            lastActivityObjectType: ActivityObjectType.PROGRAMMES,
            lastActivityObjectID: programme.id,
          });
        }
        return;
      }
      console.warn(
        "Don't know what to do with this card! Should never happen.",
      );
    },
    [
      createContentEntry,
      currentHealthProGroup,
      friendlyUsername,
      navigate,
      programme,
      programmeId,
      programmePlan,
      space?.id,
      tracker,
      updateProgramme,
      userProfile,
    ],
  );

  const afterCardDelete = useCallback(
    async (cardId: string, laneId: string) => {
      if (!programme) {
        return;
      }
      try {
        const entry = programmeEntryByEntryID(programme, cardId);
        if (!entry) {
          return;
        }
        if (entry.programmeEntryType === EntryType.NOTE) {
          await deleteNote({ id: entry.objectID, parentID: programmeId });
        }
        if (entry.programmeEntryType === EntryType.CONTENT_ENTRY) {
          await deleteContentEntry({
            id: entry.objectID,
            parentID: programmeId,
          });
        }
      } finally {
        const updatedPlanDays = programmePlan.days.map((day) => {
          if (day.id !== laneId) {
            return day;
          }
          const updatedEntries = day.entries.filter((e) => e.id !== cardId);
          return {
            ...day,
            entries: updatedEntries,
          };
        });
        const updatedProgramme = programmeWithPlanUpdated(programme, {
          ...programmePlan,
          days: updatedPlanDays,
        });
        reportAccountActivitySignalOperation({
          spaceID: space?.id,
          friendlyUsername,
          lastActivityObjectType: ActivityObjectType.PROGRAMMES,
          lastActivityObjectID: programme.id,
        });
        await updateProgramme(updatedProgramme);
      }
    },
    [
      deleteContentEntry,
      deleteNote,
      friendlyUsername,
      programme,
      programmeId,
      programmePlan,
      space?.id,
      updateProgramme,
    ],
  );

  const isMobile = useIsMobile();

  const isLoading =
    isUserProfileLoading ||
    isSpaceLoading ||
    isOwnedProgrammesLoading ||
    isDistilledMealsByParentsLoading ||
    notesLoading ||
    contentEntriesLoading ||
    sharedContentBoardsLoading ||
    isSharedContentEntriesByParentsLoading ||
    isGrcRecipesLoading;

  if (isLoading) {
    return <ResourceLoadingModal />;
  }

  return (
    <>
      <div
        className={classNames('smorg-board-container', {
          'with-fixed-header': hasNutritionHeader,
        })}
        id="programme-board">
        <Board
          editable
          appBoardId={programmePlan.id}
          data={reactTrelloData}
          components={components}
          onCardAdd={afterCardAdd}
          onCardClick={onCardClick}
          onCardDelete={afterCardDelete}
          handleDragEnd={afterCardDrag}
          style={isMobile ? mobileBoardCss : desktopBoardCss}
          backdropStyle={isMobile ? mobileHeightRule : desktopHeightRule}
          t={customTranslation}
        />
      </div>
      {detailVisibleFor?.entryType === EntryType.MEAL && (
        <MealDetailContainer
          parentSmorgBoardId={detailVisibleFor.parentID}
          menuId={null}
          mealId={detailVisibleFor.objectID}
          visible
          dismiss={() => navigate(-1)}
        />
      )}
      {detailVisibleFor?.entryType === EntryType.GRC_RECIPE && (
        <GRCRecipeDetailContainer
          grcRecipeID={detailVisibleFor.objectID}
          visible
          dismiss={() => navigate(-1)}
        />
      )}
      {detailVisibleFor?.entryType === EntryType.NOTE && (
        <NoteDetailContainer
          noteId={detailVisibleFor.objectID}
          visible
          dismiss={() => navigate(-1)}
        />
      )}
      {detailVisibleFor?.entryType === EntryType.CONTENT_ENTRY && (
        <ContentEntryDetailContainer
          parentSmorgBoardId={detailVisibleFor.parentID}
          contentEntryID={detailVisibleFor.objectID}
          visible
          dismiss={() => navigate(-1)}
        />
      )}
      {detailVisibleFor?.entryType ===
        EntryType.SHARED_CONTENT_ENTRY_REFERENCE && (
        <ContentEntryDetailContainer
          parentSmorgBoardId={detailVisibleFor.parentID}
          contentEntryID={detailVisibleFor.objectID}
          visible
          dismiss={() => navigate(-1)}
        />
      )}
    </>
  );
};

export default ProgrammePlanBoard;
